import { useEffect, useRef, useState, useCallback } from "react";
import { FiSend, FiImage, FiX, FiSmile, FiSearch, FiMic, FiInfo, FiChevronDown, FiPaperclip, FiTrash2, FiArrowLeft, FiPlus, FiFile } from "react-icons/fi";
import toast from "react-hot-toast";
import useAuthStore from "../../store/authStore";
import useChatStore from "../../store/chatStore";
import { useSocket } from "../../context/SocketContext";
import useTyping from "../../hooks/useTyping";
import MessageBubble from "./MessageBubble";
import EmojiPicker from "./EmojiPicker";
import GifPicker from "./GifPicker";
import ReplyPreview from "./ReplyPreview";
import PinBar from "./PinBar";
import MessageSearchBar from "./MessageSearchBar";
import VoiceRecorder from "./VoiceRecorder";
import GroupInfoPanel from "./GroupInfoPanel";
import UserInfoPanel from "./UserInfoPanel";
import {
  getConversationName, getConversationAvatar, getInitials,
  formatLastSeen, getOtherParticipant, isConversationOnline,
} from "../../utils/helpers";
import styles from "./ChatWindow.module.css";

const playSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator(), gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.1);
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
    osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.3);
  } catch (_) {}
};

// Compress image before sending (max 800px, 80% quality)
const compressImage = (dataUrl, maxPx = 800, quality = 0.82) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      let { width, height } = img;
      const scale = Math.min(1, maxPx / Math.max(width, height));
      canvas.width = Math.round(width * scale);
      canvas.height = Math.round(height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.src = dataUrl;
  });

const ChatWindow = ({ onBack }) => {
  const { user } = useAuthStore();
  const {
    activeConversation, messages, isLoadingMessages,
    sendMessage, sendVoiceMessage, getTypingInConversation, onlineUsers, groupActivities, clearChat,
  } = useChatStore();
  const socketRef = useSocket();
  const { startTyping, stopTyping } = useTyping(activeConversation?._id);

  const [text, setText] = useState("");
  const [imagePreview, setImagePreview] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const emojiBtnRef = useRef(null);
  const gifBtnRef   = useRef(null);
  const [showGif, setShowGif]     = useState(false);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [showSearch, setShowSearch] = useState(false);
  const [showVoice, setShowVoice] = useState(false);
  const [showGroupInfo, setShowGroupInfo] = useState(false);
  const [showUserInfo, setShowUserInfo] = useState(false);
  const [highlightId, setHighlightId] = useState(null);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const messagesBoxRef = useRef(null);
  const [filePreview, setFilePreview] = useState(null); // { name, size, type, base64 }
  const [videoPreview, setVideoPreview] = useState(null); // { name, size, type, base64 }
  const fileDocRef   = useRef(null);
  const fileVideoRef  = useRef(null);
  const prevMsgCount = useRef(0);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const messageRefs = useRef({});

  const name = getConversationName(activeConversation, user._id);
  const avatar = getConversationAvatar(activeConversation, user._id);
  const online = isConversationOnline(activeConversation, user._id, onlineUsers);
  const other = getOtherParticipant(activeConversation, user._id);
  const typing = getTypingInConversation(activeConversation?._id);
  const pinnedMessages = activeConversation?.pinnedMessages || [];
  const isGroup = activeConversation?.isGroup;

  // Count online members for group header
  const onlineMemberCount = isGroup
    ? (activeConversation?.participants || []).filter((p) => {
        const pid = p._id || p;
        return onlineUsers.includes(pid);
      }).length
    : 0;

  // Keyboard shortcut: Ctrl+K = search, Escape = close search
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        setShowSearch((p) => !p);
      }
      if (e.key === "Escape") setShowSearch(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    if (activeConversation) {
      socketRef?.current?.emit("conversation:join", activeConversation._id);
      // Tell the sender their messages have been seen (blue ticks)
      socketRef?.current?.emit("message:read", {
        conversationId: activeConversation._id,
        userId: user._id,
      });
    }
    return () => { /* intentionally not leaving room — must keep receiving messages */ };
  }, [activeConversation?._id]);

  useEffect(() => {
    if (messages.length === 0) return;
    const lastMsg = messages[messages.length - 1];
    const isOwnMessage = lastMsg?.sender?._id === user._id;
    const messagesBox = messagesBoxRef.current;

    if (messagesBox) {
      const distFromBottom = messagesBox.scrollHeight - messagesBox.scrollTop - messagesBox.clientHeight;
      // Auto-scroll only if: own message OR user is already near bottom (within 150px)
      if (isOwnMessage || distFromBottom < 150) {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }

    // Play sound for incoming messages
    if (messages.length > prevMsgCount.current) {
      const last = messages[messages.length - 1];
      if (last && last.sender?._id !== user._id) playSound();
    }
    prevMsgCount.current = messages.length;
  }, [messages]);


  // Scroll to bottom when typing indicator appears
  useEffect(() => {
    if (typing.length > 0) {
      const messagesBox = messagesBoxRef.current;
      if (messagesBox) {
        const distFromBottom = messagesBox.scrollHeight - messagesBox.scrollTop - messagesBox.clientHeight;
        if (distFromBottom < 200) {
          messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }
      }
    }
  }, [typing.length]);

  useEffect(() => {
    setReplyTo(null); setShowSearch(false); setShowVoice(false);
    setShowEmoji(false); setText(""); setImagePreview(null);
    setShowGroupInfo(false);
    setShowUserInfo(false);
  }, [activeConversation?._id]);

  const scrollToMessage = useCallback((msgId) => {
    const el = messageRefs.current[msgId];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightId(msgId);
      setTimeout(() => setHighlightId(null), 2000);
    }
  }, []);

  const handleSend = async (e) => {
    e.preventDefault();
    if ((!text.trim() && !imagePreview && !filePreview && !videoPreview) || isSending) return;
    setIsSending(true); stopTyping(); setShowEmoji(false);
    const replyId = replyTo?._id; setReplyTo(null);
    try {
      const msg = await sendMessage(
        activeConversation._id, text.trim(), imagePreview, replyId,
        filePreview ? { file: filePreview.base64, fileName: filePreview.name, fileSize: filePreview.size, fileType: filePreview.type }
        : videoPreview ? { file: videoPreview.base64, fileName: videoPreview.name, fileSize: videoPreview.size, fileType: videoPreview.type }
        : null
      );
      socketRef?.current?.emit("message:send", { conversationId: activeConversation._id, message: msg });
      setText(""); setImagePreview(null); setFilePreview(null); setVideoPreview(null);
    } catch { toast.error("Failed to send message"); }
    finally { setIsSending(false); }
  };

  const handleVoiceSend = async (audioData, duration) => {
    try {
      const msg = await sendVoiceMessage(activeConversation._id, audioData, duration);
      setShowVoice(false);
      socketRef?.current?.emit("message:send", { conversationId: activeConversation._id, message: msg });
    } catch (err) {
      console.error("Voice send error:", err);
      toast.error("Failed to send voice message");
      throw err; // re-throw so VoiceRecorder can reset sending state
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) return toast.error("Image must be under 20MB");
    const reader = new FileReader();
    reader.onloadend = async () => {
      const compressed = await compressImage(reader.result);
      setImagePreview(compressed);
    };
    reader.readAsDataURL(file);
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // 16MB limit like WhatsApp
    if (file.size > 16 * 1024 * 1024) return toast.error("Video must be under 16MB");
    const reader = new FileReader();
    reader.onloadend = () => {
      setVideoPreview({ base64: reader.result, name: file.name, size: file.size, type: file.type });
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleDocChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) return toast.error("File must be under 20MB");
    const reader = new FileReader();
    reader.onloadend = () => setFilePreview({
      name: file.name, size: file.size, type: file.type, base64: reader.result
    });
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(e); }
  };

  // Close attach menu on outside click
  useEffect(() => {
    if (!showAttachMenu) return;
    const handler = (e) => {
      if (!e.target.closest('[data-attachmenu]')) setShowAttachMenu(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showAttachMenu]);

  const handleGifSelect = async (gifUrl) => {
    setShowGif(false);
    setIsSending(true);
    try {
      const msg = await sendMessage(activeConversation._id, "", null, null, null, gifUrl);
      socketRef?.current?.emit("message:send", {
        conversationId: activeConversation._id,
        message: msg,
      });
    } catch {
      toast.error("Failed to send GIF");
    } finally {
      setIsSending(false);
    }
  };

  const handleEmojiSelect = (emoji) => {
    const ta = textareaRef.current;
    if (ta) {
      const s = ta.selectionStart, end = ta.selectionEnd;
      const newText = text.slice(0, s) + emoji + text.slice(end);
      setText(newText);
      setTimeout(() => { ta.selectionStart = s + emoji.length; ta.selectionEnd = s + emoji.length; ta.focus(); }, 0);
    } else setText((p) => p + emoji);
  };

  return (
    <div className={`${styles.windowContainer} ${(showGroupInfo || showUserInfo) ? styles.withPanel : ""}`}>
      <div className={styles.window}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            {/* Back button — mobile only */}
            {onBack && (
              <button className={`${styles.headerBtn} ${styles.backBtn}`} onClick={onBack} title="Back">
                <FiArrowLeft size={18} />
              </button>
            )}
            <div className="avatar-wrapper" style={{ cursor: "pointer" }}
              onClick={() => isGroup ? setShowGroupInfo((p) => !p) : setShowUserInfo((p) => !p)}>
              <div className="avatar" style={{ width: 40, height: 40, fontSize: 14 }}>
                {avatar ? <img src={avatar} alt={name}  /> : getInitials(name)}
              </div>
              {online && <span className="online-dot" />}
            </div>
            <div style={{ cursor: "pointer" }}
              onClick={() => isGroup ? setShowGroupInfo((p) => !p) : setShowUserInfo((p) => !p)}>
              <div className={styles.headerName}>{name}</div>
              <div className={styles.headerStatus}>
                {isGroup ? (
                  <>
                    {activeConversation.participants.length} members
                    {onlineMemberCount > 0 && (
                      <span className={styles.onlineCount}> · {onlineMemberCount} online</span>
                    )}
                  </>
                ) : online ? "Online"
                  : other?.lastSeen ? `Last seen ${formatLastSeen(other.lastSeen)}` : "Offline"}
              </div>
            </div>
          </div>

          <div className={styles.headerActions}>
            {/* Search — always */}
            <button
              className={`${styles.headerBtn} ${showSearch ? styles.headerBtnActive : ""}`}
              onClick={() => setShowSearch((p) => !p)} title="Search messages">
              <FiSearch size={16} />
            </button>

            {/* Clear chat */}
            <button
              className={styles.clearBtn}
              title="Clear chat"
              onClick={() => setShowClearConfirm(true)}
            >
              <FiTrash2 size={16} />
            </button>
            {/* Group: info icon to toggle panel | 1-on-1: block icon ALWAYS visible */}
            {isGroup ? (
              <button
                className={`${styles.headerBtn} ${showGroupInfo ? styles.headerBtnActive : ""}`}
                onClick={() => setShowGroupInfo((p) => !p)}
                title="Group info">
                <FiInfo size={17} />
              </button>
            ) : (
              <button
                className={`${styles.headerBtn} ${showUserInfo ? styles.headerBtnActive : ""}`}
                onClick={() => setShowUserInfo((p) => !p)}
                title="Contact info">
                <FiInfo size={17} />
              </button>
            )}
          </div>
        </div>

        {showSearch && <MessageSearchBar onClose={() => setShowSearch(false)} onJump={scrollToMessage} />}
        {pinnedMessages.length > 0 && <PinBar pinnedMessages={pinnedMessages} onJump={scrollToMessage} />}

        {/* Messages */}
        <div
          className={styles.messages}
          ref={messagesBoxRef}
          onClick={() => setShowEmoji(false)}
          onScroll={(e) => {
            const el = e.currentTarget;
            const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
            setShowScrollBtn(distFromBottom > 150);
          }}
        >
          {isLoadingMessages ? (
            <div className={styles.loading}>Loading messages...</div>
          ) : messages.length === 0 ? (
            <div className={styles.emptyMessages}>
              <div className={styles.emptyIcon}>👋</div>
              <p>Say hello to {name}!</p>
            </div>
          ) : (
            messages.map((msg, idx) => {
              // Date separator
              const msgDate = new Date(msg.createdAt).toDateString();
              const prevDate = idx > 0 ? new Date(messages[idx-1].createdAt).toDateString() : null;
              const showDate = idx === 0 || msgDate !== prevDate;
              const today = new Date().toDateString();
              const yesterday = new Date(Date.now() - 86400000).toDateString();
              // WhatsApp-style: show actual time for today's messages (not "Today"),
              // "Yesterday" for yesterday, or full date for older
              const dateLabel = msgDate === today
                ? new Date(msg.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                : msgDate === yesterday
                ? "Yesterday"
                : new Date(msg.createdAt).toLocaleDateString([], { day: "numeric", month: "long", year: "numeric" });
              return (
              <div key={msg._id}>
                {showDate && (
                  <div className={styles.dateSeparator}>
                    <span className={styles.dateLabel}>{dateLabel}</span>
                  </div>
                )}
                <div
                  className={highlightId === msg._id ? styles.highlighted : ""}
                  ref={(el) => { if (el) messageRefs.current[msg._id] = el; }}
                >
                  <MessageBubble
                    message={msg}
                    isOwn={msg.sender._id === user._id}
                    showAvatar={idx === 0 || messages[idx - 1]?.sender._id !== msg.sender._id}
                    conversationId={activeConversation._id}
                    onReply={setReplyTo}
                  />
                </div>
              </div>
            );
            })
          )}
          {/* Group activity messages (joined/left) */}
          {isGroup && groupActivities
            .filter((a) => a.conversationId === activeConversation._id)
            .map((a) => (
              <div key={a.id} className={styles.activityMsg}>
                <span>{a.text}</span>
                <span className={styles.activityTime}>
                  {a.time.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </span>
              </div>
            ))}

          {typing.length > 0 && (
            <div className={styles.typingIndicator}>
              <div className={styles.typingDots}><span /><span /><span /></div>
              <span className={styles.typingText}>{typing[0].userName} is typing...</span>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {imagePreview && (
          <div className={styles.imagePreview}>
            <img src={imagePreview} alt="preview" />
            <button onClick={() => setImagePreview(null)} className={styles.removeImage}><FiX size={14} /></button>
          </div>
        )}

        {/* Scroll to bottom button */}
        {showScrollBtn && (
          <button
            className={styles.scrollToBottom}
            onClick={() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })}
            title="Scroll to bottom"
          >
            <FiChevronDown size={20} />
          </button>
        )}

        {/* File preview */}
        {videoPreview && (
          <div className={styles.filePreviewBar}>
            <span className={styles.filePreviewIcon}>🎬</span>
            <span className={styles.filePreviewName}>{videoPreview.name}</span>
            <span className={styles.filePreviewSize}>({(videoPreview.size/1024/1024).toFixed(1)} MB)</span>
            <button className={styles.fileRemoveBtn} onClick={() => setVideoPreview(null)} title="Remove video">
              <FiX size={12}/>
            </button>
          </div>
        )}
        {filePreview && (
          <div className={styles.filePreviewBar}>
            <span className={styles.filePreviewIcon}>📎</span>
            <span className={styles.filePreviewName}>{filePreview.name}</span>
            <span className={styles.filePreviewSize}>({(filePreview.size/1024).toFixed(1)} KB)</span>
            <button className={styles.fileRemoveBtn} onClick={() => setFilePreview(null)} title="Remove file">
              <FiX size={12}/>
            </button>
          </div>
        )}

        {replyTo && <ReplyPreview message={replyTo} onCancel={() => setReplyTo(null)} />}
        {showEmoji && <EmojiPicker onSelect={handleEmojiSelect} onClose={() => setShowEmoji(false)} triggerRef={emojiBtnRef} />}
        {showGif && <GifPicker onSelect={handleGifSelect} onClose={() => setShowGif(false)} triggerRef={gifBtnRef} />}

        {/* Input */}
        <form className={styles.inputArea} onSubmit={handleSend}>
          <input type="file" ref={fileInputRef} accept="image/*" style={{ display: "none" }} onChange={handleImageChange} />
              <input type="file" ref={fileDocRef} accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar,.csv" style={{ display: "none" }} onChange={handleDocChange} />
              <input type="file" ref={fileVideoRef} accept="video/mp4,video/webm,video/ogg,video/quicktime" style={{ display: "none" }} onChange={handleVideoChange} />
          {showVoice ? (
            <VoiceRecorder onSend={handleVoiceSend} onCancel={() => setShowVoice(false)} />
          ) : (
            <>
              {/* + Attach menu button */}
              <div className={styles.attachMenuWrap} data-attachmenu="true">
                <button type="button"
                  className={`${styles.plusBtn} ${showAttachMenu ? styles.plusBtnActive : ""}`}
                  onClick={() => { setShowAttachMenu(p => !p); setShowEmoji(false); setShowGif(false); }}
                  title="Attach"
                >
                  <FiPlus size={20} />
                </button>
                {showAttachMenu && (
                  <div className={styles.attachMenu}>
                    <button type="button" className={styles.attachMenuItem}
                      onClick={() => { fileInputRef.current?.click(); setShowAttachMenu(false); }}>
                      <span className={styles.attachMenuIcon} style={{background:"#2196f3"}}>🖼️</span>
                      <span>Photo</span>
                    </button>
                    <button type="button" className={styles.attachMenuItem}
                      onClick={() => { fileDocRef.current?.click(); setShowAttachMenu(false); }}>
                      <span className={styles.attachMenuIcon} style={{background:"#7c5cbf"}}>📄</span>
                      <span>Document</span>
                    </button>
                    <button type="button" className={styles.attachMenuItem}
                      ref={gifBtnRef} onClick={() => { setShowGif(p => !p); setShowAttachMenu(false); }}>
                      <span className={styles.attachMenuIcon} style={{background:"#ff5722"}}>GIF</span>
                      <span>GIF</span>
                    </button>
                    <button type="button" className={styles.attachMenuItem}
                      onClick={() => { fileVideoRef.current?.click(); setShowAttachMenu(false); }}>
                      <span className={styles.attachMenuIcon} style={{background:"#e91e63"}}>🎬</span>
                      <span>Video</span>
                    </button>
                  </div>
                )}
              </div>
              {/* Emoji stays inline in message bar */}
              <button ref={emojiBtnRef} type="button" className={`${styles.attachBtn} ${showEmoji ? styles.attachBtnActive : ""}`} onClick={() => { setShowEmoji((p) => !p); setShowGif(false); setShowAttachMenu(false); }} title="Emoji"><FiSmile size={18} /></button>
              <textarea ref={textareaRef} className={styles.textInput} placeholder="Type a message..."
                value={text} onChange={(e) => { setText(e.target.value); startTyping(); }}
                onKeyDown={handleKeyDown} rows={1} />
              <button type="button" className={`${styles.attachBtn} ${styles.micBtnStyle}`} onClick={() => setShowVoice(true)} title="Voice message"><FiMic size={17} /></button>
              <button type="submit" className={styles.sendBtn} disabled={(!text.trim() && !imagePreview && !filePreview && !videoPreview) || isSending}><FiSend size={17} /></button>
            </>
          )}
        </form>
      </div>

      {/* Group Info side panel */}
      {showGroupInfo && isGroup && (
        <GroupInfoPanel onClose={() => setShowGroupInfo(false)} />
      )}

      {/* 1-on-1 User info panel */}
      {showUserInfo && !isGroup && (
        <UserInfoPanel other={other} onClose={() => setShowUserInfo(false)} />
      )}

      {/* Clear chat confirmation modal */}
      {showClearConfirm && (
        <div className={styles.confirmOverlay} onClick={() => setShowClearConfirm(false)}>
          <div className={styles.confirmModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.confirmIcon}>🧹</div>
            <h3>Clear chat?</h3>
            <p>All messages will be deleted for you only. This cannot be undone.</p>
            <div className={styles.confirmActions}>
              <button className={styles.confirmCancel} onClick={() => setShowClearConfirm(false)}>
                Cancel
              </button>
              <button
                className={styles.confirmDelete}
                onClick={async () => {
                  setShowClearConfirm(false);
                  await clearChat(activeConversation._id);
                  toast.success("Chat cleared");
                }}
              >
                Yes, Clear
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatWindow;
